
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useState, useEffect } from 'react';
import { Loader2 } from 'lucide-react';

interface PersonalInfoFormProps {
  initialValues: {
    fullName: string;
    email: string;
    company: string;
    designation: string;
    consentGiven: boolean;
  };
  onSubmit: (values: PersonalInfoFormProps['initialValues']) => Promise<void>;
  onBack: () => void;
  isSubmitting?: boolean;
}

const PersonalInfoForm = ({ initialValues, onSubmit, onBack, isSubmitting = false }: PersonalInfoFormProps) => {
  const [values, setValues] = useState(initialValues);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Sync values when initialValues change (e.g., after restart)
  useEffect(() => {
    setValues(initialValues);
    setErrors({});
  }, [initialValues.fullName, initialValues.email, initialValues.company, initialValues.designation, initialValues.consentGiven]);
  
  const handleChange = (field: keyof typeof values, value: string | boolean) => {
    setValues(prev => ({ ...prev, [field]: value }));
    
    // Clear error when field is changed
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };
  
  const validate = () => {
    const newErrors: Record<string, string> = {};
    
    if (!values.fullName) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!values.email) {
      newErrors.email = 'Email is required';
    } else if (!/^\S+@\S+\.\S+$/.test(values.email)) {
      newErrors.email = 'Please enter a valid email address';
    }
    
    if (!values.consentGiven) {
      newErrors.consentGiven = 'You must agree to the terms';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validate()) {
      setIsProcessing(true);
      try {
        await onSubmit(values);
      } finally {
        setIsProcessing(false);
      }
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <Card className="border-none shadow-lg">
        <CardHeader className="bg-[#e87a2e] text-white rounded-t-lg">
          <CardTitle>Almost Done!</CardTitle>
          <CardDescription className="text-white/80">
            Please provide your information to complete the assessment
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="pt-6">
            <div className="grid gap-6">
              <div className="grid gap-2">
                <Label htmlFor="fullName">Full Name *</Label>
                <Input 
                  id="fullName" 
                  value={values.fullName} 
                  onChange={e => handleChange('fullName', e.target.value)}
                />
                {errors.fullName && (
                  <p className="text-sm text-destructive">{errors.fullName}</p>
                )}
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="email">Email Address *</Label>
                <Input 
                  id="email" 
                  type="email" 
                  value={values.email} 
                  onChange={e => handleChange('email', e.target.value)}
                />
                {errors.email && (
                  <p className="text-sm text-destructive">{errors.email}</p>
                )}
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="company">Company</Label>
                <Input 
                  id="company" 
                  value={values.company} 
                  onChange={e => handleChange('company', e.target.value)}
                />
              </div>
              
              <div className="grid gap-2">
                <Label htmlFor="designation">Job Title / Designation</Label>
                <Input 
                  id="designation" 
                  value={values.designation} 
                  onChange={e => handleChange('designation', e.target.value)}
                />
              </div>
              
              <div className="flex items-start space-x-2 pt-2">
                <Checkbox 
                  id="consent" 
                  checked={values.consentGiven}
                  onCheckedChange={checked => handleChange('consentGiven', checked === true)}
                />
                <div className="grid gap-1.5 leading-none">
                  <Label 
                    htmlFor="consent" 
                    className="text-sm font-normal leading-snug"
                  >
                    I agree that my information may be used to contact me about my assessment results. *
                  </Label>
                  {errors.consentGiven && (
                    <p className="text-sm text-destructive">{errors.consentGiven}</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
          
          <CardFooter className="flex justify-between border-t p-4">
            <Button
              type="button"
              variant="outline"
              onClick={onBack}
            >
              Back
            </Button>
            
            <Button
              type="submit"
              className="bg-[#e87a2e] hover:bg-[#d16518] text-white"
              disabled={isProcessing || isSubmitting}
            >
              {(isProcessing || isSubmitting) ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Assessment'
              )}
            </Button>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default PersonalInfoForm;
